<template>
  <div class="milestone-wrapper">
    <Dropdown
      position="bottom-right"
      max-height="0"
      :closeable="closeable"
      v-if="milestones"
      class="milestone-started hide-xs"
      :class="{ showing: visibility, hide: !visibility }"
      ref="dd">
      <template slot="toggle">
        <button class="btn btn-primary milestone-toggle free-height strong font-h5s py-10 px-10 shadow" :class="{ 'glow-button': closeable }">
          <span class="text">👋 Getting started</span>
          <span class="shimmer"></span>
        </button>
      </template>
      <template slot="content">
        <!-- CHECK LIST -->
        <div class="milestone-checklist" v-if="closeable">
          <div class="pr-20">
            <h5 class="strong">Hey {{ settings.name }} 👋</h5>
            <h6>Let's finish your onboarding. Click on each bulletpoint for more info.</h6>
            <div class="progress bg-lightblur big-style no-border">
              <div class="progress-bar bg-primary" :style="`width: ${percent_progress}%`"></div>
            </div>
          </div>

          <perfect-scrollbar style="max-height: calc(100vh - 300px)" class="mt-20 pr-20">
            <div class="checklist font-h6" v-for="checklist in checklists" :key="checklist.level">
              <h6 class="strong">Level: {{ checklist.level }}</h6>
              <div class="task" :class="{ checked: task.checked }" v-for="task in checklist.tasks" :key="task.name" @click="goTour(task)">
                <i class="la la-circle icon-uncheck"></i>
                <i class="la la-check-circle icon-checked"></i>
                <div class="task-name">
                  <span>{{ task.name }}</span>
                </div>
              </div>
            </div>
          </perfect-scrollbar>
        </div>

        <!-- 3 IMPORTANT STEPS -->
        <div class="milestone-main p-10" v-else :class="{ narrow: is_narrow }">
          <div class="flex items-center mb-5">
            <h5 class="strong mb-0">Hey {{ settings.name }} 👋</h5>
            <div class="flex-1"></div>
            <span v-if="narrowable" class="pointer font-h5 narrow-milestone" @click="is_narrow = !is_narrow">
              <i class="la la-caret-down"></i>
            </span>
          </div>
          <h6 class="mb-20">Let's finish {{ number_important_steps }} very important {{ number_important_steps > 1 ? "steps" : "step" }}.</h6>

          <div class="milestone-steps">
            <!-- Active email -->
            <div
              class="item glow-button"
              @click="resendEmail"
              v-if="!milestones.active_email.checked"
              content="Resend activation email"
              v-tippy="{
                placement: 'top',
                arrow: true,
              }">
              <i class="la la-envelope font-h3"></i>
              <h6 class="flex-1 mb-0 px-10">Active your email!</h6>
              <div class="button-round bg-primary">
                <i v-if="confirm_sending" class="la la-spinner spinning"></i>
                <i v-else class="la la-envelope"></i>
              </div>
              <span class="shimmer"></span>
            </div>

            <!-- Install extension -->
            <a
              v-if="isDesktopChrome() && !status_extension"
              class="item"
              :class="{ 'glow-button': milestones.active_email.checked }"
              target="_blank"
              href="https://chromewebstore.google.com/detail/screen-recorder-schedulin/kijbihhjhdiipiljfhboknmdipgphoel"
              v-ripple>
              <i class="la la-chrome font-h3"></i>
              <h6 class="flex-1 mb-0 px-10">Install Weezly Chrome Extension</h6>
              <div class="button-round bg-primary">
                <i class="la la-download"></i>
              </div>
              <span class="shimmer"></span>
            </a>

            <!-- Enable notifications -->
            <Notification @updateStatus="updateStatusNotification" :class="{ 'glow-button': milestones.active_email.checked && status_extension }" />

            <!-- Watch tutorials -->
            <div class="mt-30" v-if="!milestones.open_tutorials.checked">
              <h6 class="strong">Watch the Weezly Tutorials</h6>

              <div
                class="inline-block border-radius"
                style="line-height: 0"
                :class="{ 'glow-button': milestones.active_email.checked && status_extension && status_notification }">
                <img src="@/assets/img/tutorials.png" class="border-radius pointer" alt="Click to watch tutorials" @click="watchTutorials" />
                <span class="shimmer"></span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Dropdown>

    <div class="milestone-notification" v-if="notification_name != ''">
      <lottie-player autoplay loop mode="normal" :src="`${origin}/lotties/milestone-completed.json`"> </lottie-player>
      <div class="notification-content">
        <h6 class="mb-5">Milestone completed 🚀</h6>
        <h5 class="strong mb-0">{{ notification_name }}</h5>
      </div>
    </div>

    <!-- Confirm send email active -->
    <Modal :value="true" class="ready-page" v-if="confirm_sent" width="800px">
      <template slot="content">
        <div class="align-center">
          <lottie-player
            autoplay
            loop
            mode="normal"
            src="https://assets8.lottiefiles.com/packages/lf20_lb7e63po.json"
            style="width: 220px"
            class="inline-block">
          </lottie-player>
          <h1>{{ $t("text.confirmation_email_sent") }}</h1>
          <h4>{{ $t("text.active_your_account") }}</h4>
        </div>
      </template>
    </Modal>

    <!-- Tutorials instruction -->
    <div v-if="open_tutorials" class="modal-wrapper">
      <div class="close-handle fade-in" style="background-color: rgba(0, 10, 30, 0.8)"></div>
      <div class="modal full-width align-center" style="width: 100%">
        <perfect-scrollbar class="full-height">
          <h1 class="strong mb-10">Welcome to Weezly tutorials.</h1>
          <h5>{{ settings.name }}, you will always find Weezly tutorials in the menu. Good luck becoming an Weezly expert!</h5>
          <div class="my-20">
            <img src="@/assets/img/show_tutorials.png" alt="" style="max-height: min(70vh, calc(100vh - 320px))" />
          </div>
          <button v-ripple class="btn btn-primary py-10 free-height font-h5 strong" @click="open_tutorials = false">Awesome, let me see the tutorials!</button>
        </perfect-scrollbar>
      </div>
    </div>
  </div>
</template>

<script>
import request from "@/service";
import Notification from "./Notification";
export default {
  name: "Milestone",
  components: {
    Notification,
  },
  props: {},
  data: function () {
    return {
      milestones: false,
      notifications: [],
      running: false,
      notification_name: "",

      status_notification: -1,
      status_extension: -1,

      confirm_sent: false,
      confirm_sending: false,
      confirm_send_already: false,

      open_tutorials: false,
      is_narrow: false,
    };
  },
  computed: {
    origin() {
      return location.origin;
    },
    settings() {
      return this.$store.getters.getSchedulingSettings;
    },
    checklists() {
      if (!this.milestones) return false;

      try {
        let basic_slugs = ["connect_calendar", "active_email", "install_extension", "enable_notifications", "upload_profile_image", "upload_company_logo"];
        let skilled_slugs = ["book_a_meeting", "propose_time", "record_video_message"];
        let jedi_slugs = ["record_single_video", "record_multi_video"];

        return [
          {
            level: "Basic",
            tasks: this.getMilestonesBySlugs(basic_slugs),
          },
          {
            level: "Skilled",
            tasks: this.getMilestonesBySlugs(skilled_slugs),
          },
          {
            level: "Jedi",
            tasks: this.getMilestonesBySlugs(jedi_slugs),
          },
        ];
      } catch (e) {
        return false;
      }
    },
    percent_progress() {
      if (!this.milestones) return 0;

      let total_checked = Object.values(this.milestones).filter((item) => item.checked).length;
      return Math.round((total_checked / Object.keys(this.milestones).length) * 100);
    },
    visibility() {
      const dropdownMenu = document.querySelector(".milestone-started .dropdown-menu");
      if (dropdownMenu) dropdownMenu.style.display = "none";

      if (!this.milestones) return false;

      if (!["/", "/screencast", "/dynamic-videos"].includes(this.$route.path)) return false;

      if ((this.status_extension == -1) | (this.status_notification == -1)) return false;
      else {
        let total_unchecked = Object.values(this.milestones).filter((item) => !item.checked).length;
        if (total_unchecked == 0 && this.status_extension == 1 && this.status_notification == 1) return false;
        else {
          setTimeout(() => {
            if (!this.closeable) this.$refs.dd.toggle();
            if (dropdownMenu) dropdownMenu.style.display = "block";
          }, 500);
          return true;
        }
      }
    },
    number_important_steps() {
      let n = 0;
      if (!this.milestones.active_email.checked) n++;
      if (!this.status_extension) n++;
      if (!this.status_notification) n++;
      if (!this.milestones.open_tutorials.checked) n++;
      return n;
    },
    narrowable() {
      let n = 0;
      if (!this.milestones.active_email.checked) n++;
      if (!this.status_extension) n++;
      if (!this.status_notification) n++;
      return n > 1;
    },
    closeable() {
      if (this.milestones.active_email.checked && this.status_extension == 1 && this.status_notification == 1 && this.milestones.open_tutorials.checked)
        return true;
      else return false;
    },
  },
  async mounted() {
    setTimeout(() => {
      if (this.isDesktopChrome()) {
        let ex = document.getElementById("weezly-extension-controller");
        if (ex) this.status_extension = 1;
        else this.status_extension = 0;
      } else {
        this.status_extension = 1;
      }
    }, 1000);

    this.$root.$on("milestone-notification", (slug) => {
      if (!this.milestones) return false;
      if (this.milestones[slug] && !this.milestones[slug].checked) this.loadData();
    });

    this.$root.$on("milestone-notification-alert", (slug) => {
      this.notifications.push(slug);
      this.runNotification();
    });

    await this.loadData();
  },
  methods: {
    getMilestoneName(slug) {
      switch (slug) {
        case "active_email":
          return "Active your email";
        case "connect_calendar":
          return "Connect your calendar";
        case "install_extension":
          return "Install Weezly Chrome Extension";
        case "enable_notifications":
          return "Enable desktop notifications";
        case "upload_profile_image":
          return "Upload your profile image";
        case "upload_company_logo":
          return "Upload your company logo";
        case "book_a_meeting":
          return "Let's others book a meeting with you";
        case "propose_time":
          return "Propose time for a meeting";
        case "record_video_message":
          return "Record a video message";
        case "record_single_video":
          return "Record single automated sales video";
        case "record_multi_video":
          return "Record multiple sales videos";
        default:
          return "";
      }
    },
    getMilestonesBySlugs(slugs) {
      let data = [];
      slugs.forEach((key) => {
        if (this.milestones[key]) {
          data.push({
            slug: key,
            name: this.getMilestoneName(key),
            checked: this.milestones[key].checked,
            notification: this.milestones[key].notification,
          });
        }
      });
      return data;
    },
    isDesktopChrome() {
      const userAgent = navigator.userAgent;
      const isChrome = userAgent.includes("Chrome") && !userAgent.includes("Mobile");
      const isNotEdge = !userAgent.includes("Edg");
      const isNotOpera = !userAgent.includes("OPR");

      return isChrome && isNotEdge && isNotOpera;
    },
    updateStatusNotification(status) {
      this.status_notification = status;
    },
    async loadData() {
      let response = await request("GET", "users/milestones");
      if (response) {
        // Insert fake data for notification and extension
        let newObj = {};

        for (let k in response.data) {
          newObj[k] = response.data[k];
          if (k === "active_email") {
            if (this.isDesktopChrome()) {
              newObj["install_extension"] = {
                checked: true,
                notification: false,
              };
            }
            newObj["enable_notifications"] = {
              checked: true,
              notification: false,
            };
          }
        }

        this.milestones = newObj;

        for (var key in this.milestones) {
          if (this.milestones[key].notification) {
            this.notifications.push(key);
          }
        }
        this.runNotification();
      }
    },
    async runNotification(check = true) {
      if (check && this.running) return false;

      if (this.notifications.length) {
        this.running = true;
        let slug = this.notifications[0];
        let name = this.getMilestoneName(slug);

        this.notification_name = name;
        await request("POST", "users/milestones/notified", {
          slug: slug,
        });
        setTimeout(() => {
          this.notification_name = "";
          this.$nextTick(() => {
            this.notifications = this.notifications.filter((s) => s != slug);
            this.runNotification(false);
          });
        }, 5000);
      } else {
        this.running = false;
      }
    },
    async resendEmail() {
      if (this.confirm_sending) return false;

      this.confirm_sending = true;
      const response = await request("GET", "users/verify/resend");
      if (response) {
        this.confirm_sent = true;
        this.confirm_send_already = true;

        setTimeout(() => {
          this.confirm_sent = false;
        }, 4000);
      }
      this.confirm_sending = false;
    },
    goTour(task) {
      if (task.checked) return false;

      if (task.slug == "connect_calendar") this.redirect("/calendar-connections");
      else if (task.slug == "upload_profile_image") this.redirect("/settings/profile");
      else if (task.slug == "upload_company_logo") this.redirect("/settings/branding");
      else if (task.slug == "propose_time") this.redirect("/timeslotpicker");
      else if (task.slug == "record_video_message") this.redirect("/screencast");
      else if (task.slug == "record_single_video") this.redirect("/create-dynamic-videos");
      else if (task.slug == "record_multi_video") this.redirect("/dynamic-videos");
    },
    redirect(path) {
      if (path == "/timeslotpicker" && this.$route.name == "Home") {
        window.location.href = location.origin + path;
      } else if (path == "/create-dynamic-videos" && this.$route.name == "Dynamic videos") {
        window.location.href = location.origin + path;
      } else if (this.$route.path !== path) {
        this.$refs.dd.close();
        this.$router.push(path).catch((e) => false);
      }
    },
    async watchTutorials() {
      this.open_tutorials = true;
      this.$router.push("/tutorials").catch((e) => false);
      await request("POST", "users/milestones/notified", {
        slug: "open_tutorials",
      });
      this.milestones.open_tutorials.checked = true;
    },
  },
};
</script>
